/**
 * OpenObserve RUM (Real User Monitoring) + browser logs initialization.
 *
 * This is an additive observability hook only. `initRum()` must never throw,
 * never block app render, and must no-op entirely when the client token is
 * not present at build time (e.g. local dev, or any environment where the
 * OPENOBSERVE_CLIENT_TOKEN org secret hasn't been wired up yet).
 */
import { openobserveLogs } from '@openobserve/browser-logs';
import { openobserveRum } from '@openobserve/browser-rum';

export function initRum() {
    try {
        if (typeof window === 'undefined') {
            return;
        }

        // OPENOBSERVE_CLIENT_TOKEN is a public, write-only RUM token injected at build time.
        const clientToken = process.env.REACT_APP_OPENOBSERVE_CLIENT_TOKEN;

        if (!clientToken) {
            return;
        }

        // The app calls several dynamically-generated *.incruiter.com backends
        // (see src/utils/helper.jsx -> generateUrl) rather than a single fixed
        // API base URL, so tracing is allowed for same-origin requests and any
        // incruiter.com subdomain instead of one hardcoded host.
        const allowedTracingUrlMatch = (url) => {
            try {
                const parsed = new URL(url, window.location.origin);
                if (parsed.origin === window.location.origin) {
                    return true;
                }
                return /(^|\.)incruiter\.com$/.test(parsed.hostname);
            } catch (error) {
                return false;
            }
        };

        const sharedConfig = {
            applicationId: 'frontend-interview',
            service: 'frontend-interview',
            clientToken,
            site: 'monitor.incruiter.com',
            organizationIdentifier: 'default',
            env: 'development',
            version: '0.0.1',
            insecureHTTP: false,
            apiVersion: 'v1',
        };

        openobserveRum.init({
            ...sharedConfig,
            trackResources: true,
            trackLongTasks: true,
            trackUserInteractions: true,
            sessionSampleRate: 100,
            sessionReplaySampleRate: 20,
            defaultPrivacyLevel: 'mask',
            allowedTracingUrls: [
                {
                    match: allowedTracingUrlMatch,
                    propagatorTypes: ['tracecontext', 'openobserve'],
                },
            ],
        });

        openobserveRum.startSessionReplayRecording();

        openobserveLogs.init({
            ...sharedConfig,
            forwardErrorsToLogs: true,
        });
    } catch (error) {
        console.warn('[RUM] initRum failed to initialize OpenObserve', error);
    }
}
